import { doc, collection } from 'firebase/firestore'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckExhibit } from '../models/SlideDeckExhibit'
import { SlideModel } from '../models/SlideModel'
import { SlideQuestion } from '../models/SlideQuestion'
import type { SlideDeckPreviewData } from '../cubits/PreviewMeetingCubit'
import type { MobxDocument } from '../firestore-mobx'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { VoiceOption } from '../models/VoiceOption'
import {
  type SlideDeckType,
  SlideDeckState,
  SlideDeckTheme,
  SlideType,
} from '../types'
import type { SlideDeckImportInput } from './SlideDeckImportSchema'
import { fetchAllMedia } from '../firestore/Media'
import type { Media } from '../models/Media'
import { MediaType } from '../types'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'

export async function importSlideDeckPreviewDataFromJson(
  repository: FirebaseRepository,
  parsed: SlideDeckImportInput
): Promise<SlideDeckPreviewData> {
  const slideDeckId = `in-memory-${Math.random().toString(36).substring(2)}`

  // 1) Create a new slide deck in memory
  const slideDeckData = {
    slideDeckName: parsed.slideDeckName,
    slideDeckDescription: parsed.slideDeckDescription,
    slideDeckTeaser: parsed.slideDeckTeaser,
    slideDeckDisciplines: parsed.slideDeckDisciplines,
    slideDeckLearningObjectives: parsed.slideDeckLearningObjectives,
    slideDeckKeyConcepts: parsed.slideDeckKeyConcepts ?? [],
    slideDeckPhraseSet: parsed.slideDeckPhraseSet ?? [],
    slideDeckType: (parsed.slideDeckType as unknown as SlideDeckType) ?? 0,
    slideDeckFeatured: false,
    slideDeckGoogleTemplateURL: '',
    slideDeckIndustries: [],
    slideDeckTags: [],
    slideDeckPrice: 150,
    slideDeckVersion: 'Imported',
    slideDeckImageURL: '',
    // from schema
    catalogIds: [],
    slideDeckState: SlideDeckState.draft,
    slideDeckTheme: SlideDeckTheme.light,
    slideDeckFree: false,
    slideDeckSlideCount: parsed.slides.length,
    updatedAt: new Date(),
    slideDeckTypeId: '',
  }

  const slideDecksCollection = collection(repository.firestore, 'slideDecks')
  const slideDeckRef = doc(slideDecksCollection, slideDeckId)

  const slideDeckDoc: MobxDocument<typeof slideDeckData> = {
    id: slideDeckId,
    data: slideDeckData,
    ref: slideDeckRef,
    fromCache: true, // In-memory, so it's like it's from cache
    hasPendingWrites: false,
    exists: true,
  }

  const slideDeck = new SlideDeck(repository, slideDeckDoc)

  // helper to map Solo Bot type string to SlideQuestionType
  const soloTypeToQuestionType = (
    t:
      | 'Solo Moderator Bot'
      | 'Solo Tutor Bot'
      | 'Solo Grader Bot'
      | 'Solo Role Play Bot'
  ): SlideQuestionType => {
    switch (t) {
      case 'Solo Moderator Bot':
        return SlideQuestionType.moderatorBot
      case 'Solo Tutor Bot':
        return SlideQuestionType.tutorBot
      case 'Solo Grader Bot':
        return SlideQuestionType.graderBot
      case 'Solo Role Play Bot':
        return SlideQuestionType.rolePlayBot
    }
  }

  // helper to validate and coerce voice id
  const toVoiceOption = (v?: string | null): VoiceOption | undefined =>
    v && (Object.values(VoiceOption) as string[]).includes(v)
      ? (v as VoiceOption)
      : undefined

  const idMap = {
    slide: {} as Record<string, string>,
    question: {} as Record<string, string>,
    exhibit: {} as Record<string, string>,
  }

  // Preload all media and index by title for globalAssetName lookups
  const mediaByTitle = new Map<string, Media>()
  try {
    const allMedia = await fetchAllMedia(repository)
    for (const m of allMedia) {
      mediaByTitle.set(m.data.mediaTitle, m)
    }
  } catch (err) {
    console.warn('Failed to fetch media for preview asset linking', err)
    // If media fetch fails (e.g., no permission), proceed without media linking
  }

  // 2) Create slides in memory
  const slidesCollection = collection(slideDeckRef, 'slides')
  const slides: SlideModel[] = parsed.slides.map((s, i) => {
    // give it a temporary id
    const slideId = `slide-${i}`
    idMap.slide[s.slideId] = slideId

    const data: Parameters<SlideModel['replaceData']>[0] = {
      slideDescription: s.slideDescription ?? '',
      slideDuration: s.slideDuration,
      slideName: s.slideName,
      slideType: s.slideType as unknown as SlideType,
      slideImageAltText: s.slideImageAltText ?? undefined,
      slideMarkdown: s.slideMarkdown ?? undefined,
      isSkippable: s.allowSkipVideo ?? false,
      slideOrder: i,
    }

    // Link global media by title if provided
    const trimmedAssetName = (s.globalAssetName ?? '').trim()
    const matchedMediaId =
      trimmedAssetName.length > 0
        ? mediaByTitle.get(trimmedAssetName)?.id
        : undefined
    if (matchedMediaId) {
      data.mediaId = matchedMediaId
    }

    if (
      (s.slideType as number) === SlideType.animatedCards &&
      s.animatedCards
    ) {
      data.slideAnimatedCardsTitle = s.animatedCards.contentTitle
      data.slideAnimatedCardsDescription = s.animatedCards.contentDescription
      data.slideAnimatedCardsItems = s.animatedCards.cards.map((c) => ({
        title: c.cardTitle,
        description: c.cardDescription,
      }))
      data.slideAnimatedCardsSubtext = s.animatedCards.contentFooter
    }

    const slideRef = doc(slidesCollection, slideId)
    const slideDoc: MobxDocument<typeof data> = {
      id: slideId,
      data,
      ref: slideRef,
      fromCache: true,
      hasPendingWrites: false,
      exists: true,
    }
    return new SlideModel(repository, slideDoc)
  })

  // 3) Create questions in memory
  const questionsCollection = collection(slideDeckRef, 'questions')
  const questions: SlideQuestion[] = []
  for (const slide of parsed.slides) {
    const newSlideId = idMap.slide[slide.slideId]
    if (slide.soloBot) {
      const questionId = `question-${questions.length}`
      const questionData = {
        question: slide.soloBot.talkTrack ?? '',
        prompt: slide.soloBot.additionalInstructions ?? undefined,
        questionType: soloTypeToQuestionType(slide.soloBot.type),
        answers: [],
        order: 0,
        slideId: newSlideId,
        voiceId: toVoiceOption(slide.soloBot.voiceId),
      }
      const questionRef = doc(questionsCollection, questionId)
      const questionDoc: MobxDocument<typeof questionData> = {
        id: questionId,
        data: questionData,
        ref: questionRef,
        fromCache: true,
        hasPendingWrites: false,
        exists: true,
      }
      questions.push(new SlideQuestion(repository, questionDoc))
    }

    if (
      slide.slideGroupTalkTrack &&
      slide.slideGroupTalkTrack.trim().length > 0
    ) {
      const questionId = `question-${questions.length}`
      const questionData = {
        question: slide.slideGroupTalkTrack,
        questionType: SlideQuestionType.talkTrack,
        answers: [],
        order: 0,
        slideId: newSlideId,
      }
      const questionRef = doc(questionsCollection, questionId)
      const questionDoc: MobxDocument<typeof questionData> = {
        id: questionId,
        data: questionData,
        ref: questionRef,
        fromCache: true,
        hasPendingWrites: false,
        exists: true,
      }
      questions.push(new SlideQuestion(repository, questionDoc))
    }

    if (slide.slideHeading && slide.slideHeading.trim().length > 0) {
      const questionId = `question-${questions.length}`
      const questionData = {
        question: slide.slideHeading,
        questionType: SlideQuestionType.title,
        answers: [],
        order: 0,
        slideId: newSlideId,
      }
      const questionRef = doc(questionsCollection, questionId)
      const questionDoc: MobxDocument<typeof questionData> = {
        id: questionId,
        data: questionData,
        ref: questionRef,
        fromCache: true,
        hasPendingWrites: false,
        exists: true,
      }
      questions.push(new SlideQuestion(repository, questionDoc))
    }

    if (slide.moderationTasks && slide.moderationTasks.length > 0) {
      for (const [i, t] of slide.moderationTasks.entries()) {
        const questionId = `question-${questions.length}`
        const questionData = {
          question: t.ask,
          prompt: t.additionalInstructions,
          questionType: SlideQuestionType.moderationTask,
          answers: [],
          order: i,
          slideId: newSlideId,
        }
        const questionRef = doc(questionsCollection, questionId)
        const questionDoc: MobxDocument<typeof questionData> = {
          id: questionId,
          data: questionData,
          ref: questionRef,
          fromCache: true,
          hasPendingWrites: false,
          exists: true,
        }
        questions.push(new SlideQuestion(repository, questionDoc))
      }
    }

    if (slide.pollQuestion) {
      const isMulti =
        Array.isArray(slide.pollAnswers) && slide.pollAnswers.length > 0
      const questionId = `question-${questions.length}`
      const questionData = {
        question: slide.pollQuestion,
        answers: isMulti ? slide.pollAnswers! : [],
        questionType: isMulti
          ? SlideQuestionType.customPoll
          : SlideQuestionType.poll,
        order: 0,
        slideId: newSlideId,
      }
      const questionRef = doc(questionsCollection, questionId)
      const questionDoc: MobxDocument<typeof questionData> = {
        id: questionId,
        data: questionData,
        ref: questionRef,
        fromCache: true,
        hasPendingWrites: false,
        exists: true,
      }
      questions.push(new SlideQuestion(repository, questionDoc))

      // update slide to attach input question ids
      const parentSlide = slides.find((s) => s.id === newSlideId)
      if (parentSlide) {
        parentSlide.doc.data.slideInputQuestionIds = [questionId]
      }
    }
  }

  // 4) Pre-quiz questions (slideId fixed)
  for (const [i, q] of parsed.slideDeckPreQuizQuestions.entries()) {
    const questionId = `question-${questions.length}`
    const questionData = {
      question: q.question,
      answers: q.answers ?? [],
      correct: q.correct,
      explanation: q.explanation ?? undefined,
      order: i,
      slideId: PRE_MEETING_QUESTION_SLIDE_ID,
      questionType:
        (q.questionType as unknown as SlideQuestionType) ??
        SlideQuestionType.multipleChoice,
    }
    const questionRef = doc(questionsCollection, questionId)
    const questionDoc: MobxDocument<typeof questionData> = {
      id: questionId,
      data: questionData,
      ref: questionRef,
      fromCache: true,
      hasPendingWrites: false,
      exists: true,
    }
    questions.push(new SlideQuestion(repository, questionDoc))
  }

  // 5) Exhibits with slide pointer mapping
  const exhibitsCollection = collection(slideDeckRef, 'exhibits')
  const exhibits: SlideDeckExhibit[] = parsed.slideDeckExhibits.map((e) => {
    const slideId = idMap.slide[e.SlideId]
    const exhibitId = `exhibit-${Math.random().toString(36).substring(2)}`
    const trimmedAssetName = (e.globalAssetName ?? '').trim()
    const mediaCandidate =
      trimmedAssetName.length > 0
        ? mediaByTitle.get(trimmedAssetName)
        : undefined
    let matchedMediaUrl: string | undefined
    let matchedMediaId: string | undefined
    let matchedMediaHasThumbnail: boolean | undefined
    if (mediaCandidate && mediaCandidate.data.mediaType === MediaType.Image) {
      matchedMediaUrl = mediaCandidate.data.mediaImageURL
      matchedMediaId = mediaCandidate.id
      matchedMediaHasThumbnail = true
    }

    const exhibitData = {
      exhibitDescription: e.exhibitDescription,
      exhibitName: e.exhibitName,
      slideId,
      exhibitAltText: e.imageAltText ? e.imageAltText : undefined,
      exhibitMarkdown: e.exhibitMarkdown ? e.exhibitMarkdown : undefined,
      exhibitURL: matchedMediaUrl,
      mediaId: matchedMediaId,
      mediaHasThumbnail: matchedMediaHasThumbnail,
      inputSources: [],
      updatedAt: new Date(),
    }
    const exhibitRef = doc(exhibitsCollection, exhibitId)
    const exhibitDoc: MobxDocument<typeof exhibitData> = {
      id: exhibitId,
      data: exhibitData,
      ref: exhibitRef,
      fromCache: true,
      hasPendingWrites: false,
      exists: true,
    }
    return new SlideDeckExhibit(repository, exhibitDoc)
  })

  return {
    slideDeck,
    slideDeckSlides: slides,
    slideDeckQuestions: questions,
    slideDeckExhibits: exhibits,
  }
}
